import {inject, Injectable} from '@angular/core';
import {Auth, GoogleAuthProvider, signInWithPopup, user} from '@angular/fire/auth';
import {UserModel} from '../models/user.model';
import {first, map, Observable, of, switchMap, tap} from 'rxjs';
import {UserService} from './user.service';
import {AuthTokenService} from './auth-token.service';
import {OrganizationService} from './organization.service';
import {ToastService} from './toast.service';
import {KeyExpirationToastComponent} from '../components/auth/key-expiration-toast/key-expiration-toast.component';
import {NavigateService} from './navigate.service';
import {LoggerService} from './logger.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly auth = inject(Auth)
  private readonly authToken = inject(AuthTokenService)
  private readonly userService = inject(UserService)
  private readonly orgService = inject(OrganizationService)
  private readonly toastService = inject(ToastService)
  private readonly logger = inject(LoggerService).get('auth-service')
  readonly currentUser: Observable<UserModel | null>
  private readonly navigate = inject(NavigateService)
  private _user?: UserModel

  constructor() {
    // Listen for Firebase authentication changes and update user info
    this.currentUser = user(this.auth).pipe(
      switchMap(firebaseUser => {
        if (!firebaseUser) {
          return of(null)
        } else if (firebaseUser.email === this._user?.email) {
          this._user.authUser = firebaseUser
          return of(this._user)
        } else {
          return this.getAppUser().pipe(
            tap(user => {
              this.logger.debug('Got user info', user)
              this._user = user
              this._user.authUser = firebaseUser
            }),
            switchMap(() => this.orgService.loadOrganizations()),
            map(() => this._user!),
            tap(user => this.checkApiKeyExpiration(user))
          )
        }
      })
    )
  }

  /**
   * Make sure we have an authentication token then request user info
   */
  private getAppUser() {
    return this.authToken.authToken.pipe(
      first(token => token !== null),
      switchMap(() => this.userService.get())
    )
  }

  private checkApiKeyExpiration(user: UserModel): void {
    if (user.apiKeyExpirationWarning) {
      this.toastService.add({
        header: 'API Key Expiring',
        component: KeyExpirationToastComponent,
        inputs: { expirationDays: user.apiKeyExpirationWarning },
        type: 'warning',
        duration: 10000
      });
    }
  }

  async loginWithGoogle() {
    const provider = new GoogleAuthProvider();
    await signInWithPopup(this.auth, provider).then(() => this.navigate.home());
  }

  /**
   * Logs out user and clears session
   */
  logout(): void {
    this.auth.signOut().then(() => {
      this.navigate.login();
    })
  }
}
